import { Component, Input, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CalculationStateService, CalculationOperation, QueuedActionType, PlanningCollision } from '../../../optimistic-planning/calculation-state.service';
import { PlanningActionService } from '../../../shared/services/planningaction.service';
import { PlanningBoardFacade } from '../../../planningBoard/services/planning-board.facade';

@Component({
  selector: 'calc-slot-indicator',
  template: `
    <div class="calc-slot" *ngIf="slotOperations.length > 0"
         (click)="onDotClick($event)"
         (mouseenter)="onHoverEnter()"
         (mouseleave)="onHoverLeave()">

      <!-- Indicator dot (reflects PLANNING status only — ring shows queued action status) -->
      <div class="calc-slot__dot"
           [ngClass]="{
             'calc-slot__dot--active': hasActive || hasQueuedExecuting,
             'calc-slot__dot--failed': !hasActive && !hasQueuedExecuting && hasFailed,
             'calc-slot__dot--warning': !hasActive && !hasQueuedExecuting && !hasFailed && hasWarnings,
             'calc-slot__dot--completed': !hasActive && !hasQueuedExecuting && !hasFailed && !hasWarnings && hasCompleted
           }">
      </div>
      <!-- Concentric ring when action is queued -->
      <div class="calc-slot__queued-ring"
           *ngIf="primaryOp?.queuedAction"
           [ngClass]="{
             'calc-slot__queued-ring--fail': primaryOp?.queuedActionStatus === 'failed' || primaryOp?.queuedActionStatus === 'skipped',
             'calc-slot__queued-ring--ok': primaryOp?.queuedActionStatus === 'completed',
             'calc-slot__queued-ring--exec': primaryOp?.queuedActionStatus === 'executing'
           }">
      </div>

      <!-- Count badge -->
      <span class="calc-slot__count" *ngIf="slotOperations.length > 1">{{ slotOperations.length }}</span>

      <!-- ═══ Popover (hover or pinned) ═══ -->
      <div class="csp" *ngIf="showPopover"
           [ngClass]="{
             'csp--pinned': pinned,
             'csp--calculating': primaryOp?.status === 'Calculating',
             'csp--completed': primaryOp?.status === 'Completed' && primaryOp?.result?.success !== false,
             'csp--failed': primaryOp?.status === 'Failed' || primaryOp?.result?.success === false
           }"
           [ngStyle]="popoverStyle"
           (click)="$event.stopPropagation()"
           (mouseenter)="onPopoverEnter()"
           (mouseleave)="onPopoverLeave()">

        <!-- Header -->
        <div class="csp__header">
          <span class="csp__status" [ngClass]="{
            'csp__status--calc': primaryOp?.status === 'Calculating',
            'csp__status--ok': primaryOp?.status === 'Completed' && primaryOp?.result?.success !== false,
            'csp__status--fail': primaryOp?.status === 'Failed' || primaryOp?.result?.success === false
          }">
            <i class="fa" [ngClass]="{
              'fa-spinner fa-spin': primaryOp?.status === 'Calculating',
              'fa-check': primaryOp?.status === 'Completed' && primaryOp?.result?.success !== false,
              'fa-exclamation-triangle': primaryOp?.result?.success === false && primaryOp?.status !== 'Failed',
              'fa-times': primaryOp?.status === 'Failed'
            }"></i>
            {{ statusLabel }}
          </span>
          <span class="csp__timing" *ngIf="primaryOp?.status === 'Calculating'">{{ timingLabel }}</span>
          <span class="csp__actions">
            <i *ngIf="pinned" class="fa fa-thumb-tack csp__pin csp__pin--active" [title]="tr('PINNED_TOOLTIP')"></i>
            <i *ngIf="!pinned" class="fa fa-thumb-tack csp__pin" [title]="tr('PIN_TOOLTIP')" (click)="onDotClick($event)"></i>
          </span>
        </div>
        <i *ngIf="pinned" class="fa fa-times csp__close-btn" (click)="closePopover($event)"></i>

        <!-- Subtitle -->
        <div class="csp__sub">{{ subtitleLine }}</div>
        <div class="csp__sub-user" *ngIf="requestedBy">Requested by {{ requestedBy }}</div>

        <!-- Body -->
        <div class="csp__body">
          <!-- Left: what was planned -->
          <div class="csp__section">
            <div class="csp__section-title">{{ primaryOp?.status === 'Calculating' ? getSectionTitle(primaryOp) : tr('SECTION_REQUEST') }}</div>
            <div *ngFor="let pb of pbDetails" class="csp__pb">
              <div class="csp__field" *ngIf="pb.orderNumber">
                <span class="csp__field-label">{{ tr('FIELD_ORDER') }}</span>
                <span class="csp__field-value csp__field-value--bold">{{ pb.orderNumber }}</span>
              </div>
              <div class="csp__field" *ngIf="pb.info">
                <span class="csp__field-label">{{ tr('FIELD_SUMMARY') }}</span>
                <span class="csp__field-value">{{ pb.info }}</span>
              </div>
              <div class="csp__field" *ngIf="pb.customerSearchName">
                <span class="csp__field-label">{{ tr('FIELD_CUSTOMER') }}</span>
                <span class="csp__field-value">{{ pb.customerSearchName }}</span>
              </div>
              <div class="csp__field" *ngIf="pb.containerName || pb.trailerName">
                <span class="csp__field-label">{{ tr('FIELD_EQUIPMENT') }}</span>
                <span class="csp__field-value csp__field-value--mono">{{ pb.containerName || pb.trailerName }}<span *ngIf="pb.containerType"> [{{ pb.containerType }}]</span></span>
              </div>
            </div>
            <div *ngIf="pbDetails.length === 0" class="csp__pb-fallback">
              <span *ngFor="let id of primaryOp?.planningBlockIds">{{ id }}</span>
            </div>
          </div>

          <!-- Right: result or shimmer -->
          <div class="csp__section" *ngIf="primaryOp?.status !== 'Calculating'">
            <!-- Completed: rich summary -->
            <ng-container *ngIf="primaryOp?.status === 'Completed'">
              <div class="csp__section-title">{{ tr('SECTION_RESULT') }}</div>

              <!-- Headline -->
              <div class="csp__result-headline" [ngClass]="{
                'csp__result-headline--ok': primaryOp?.result?.success !== false && !summary?.changes?.length,
                'csp__result-headline--error': primaryOp?.result?.success === false,
                'csp__result-headline--warn': primaryOp?.result?.success !== false && summary?.changes?.length
              }">
                <i *ngIf="primaryOp?.result?.success === false" class="fa fa-exclamation-triangle" style="margin-right:4px;"></i>
                {{ summary?.headline || tr('DEFAULT_SUCCESS') }}
              </div>

              <!-- TO info -->
              <div class="csp__field" *ngIf="summary?.toId">
                <span class="csp__field-label">{{ tr('FIELD_TO') }}</span>
                <span class="csp__field-value csp__field-value--bold">{{ summary.toId }}</span>
                <span class="csp__field-meta" *ngIf="summary.toStatus"> · {{ summary.toStatus }}</span>
              </div>

              <!-- Date -->
              <div class="csp__field" *ngIf="summary?.actualDate">
                <span class="csp__field-label">{{ tr('FIELD_DATE') }}</span>
                <span class="csp__field-value">{{ summary.actualDate }}</span>
              </div>

              <!-- Route -->
              <div class="csp__field" *ngIf="summary?.route">
                <span class="csp__field-label">{{ tr('FIELD_SUMMARY') }}</span>
                <span class="csp__field-value">{{ summary.route }}</span>
              </div>

              <!-- Customer -->
              <div class="csp__field" *ngIf="summary?.customer">
                <span class="csp__field-label">{{ tr('FIELD_CUSTOMER') }}</span>
                <span class="csp__field-value">{{ summary.customer }}</span>
              </div>

              <!-- Equipment -->
              <div class="csp__field" *ngIf="summary?.equipment">
                <span class="csp__field-label">{{ tr('FIELD_EQUIPMENT') }}</span>
                <span class="csp__field-value csp__field-value--mono">{{ summary.equipment }}</span>
              </div>

              <!-- Time window -->
              <div class="csp__field" *ngIf="summary?.timeWindow">
                <span class="csp__field-label">{{ tr('FIELD_TIME') }}</span>
                <span class="csp__field-value">{{ summary.timeWindow }}</span>
              </div>

              <!-- Truck / Driver -->
              <div class="csp__field" *ngIf="summary?.actualTruck || summary?.actualDriver">
                <span class="csp__field-label">{{ tr('FIELD_ASSIGNED') }}</span>
                <span class="csp__field-value">
                  <span *ngIf="summary.actualDriver">{{ summary.actualDriver }}</span>
                  <span *ngIf="summary.actualDriver && summary.actualTruck"> · </span>
                  <span *ngIf="summary.actualTruck">{{ summary.actualTruck }}</span>
                </span>
              </div>

              <!-- Changes (if any) -->
              <div class="csp__result-changes" *ngIf="summary?.changes?.length">
                <div *ngFor="let change of summary.changes" class="csp__result-change"
                     [ngClass]="{
                       'csp__result-change--ok': change.startsWith('✓'),
                       'csp__result-change--error': change.startsWith('✗')
                     }">
                  <i class="fa" [ngClass]="{
                    'fa-check-circle': change.startsWith('✓'),
                    'fa-times-circle': change.startsWith('✗'),
                    'fa-exclamation-circle': !change.startsWith('✓') && !change.startsWith('✗')
                  }"></i> {{ change }}
                </div>
              </div>
            </ng-container>

            <!-- Failed: collision or generic error -->
            <ng-container *ngIf="primaryOp?.status === 'Failed'">
              <ng-container *ngIf="primaryCollisions.length > 0; else genericError">
                <div class="csp__section-title">{{ tr('SECTION_COLLISION') }}</div>
                <div *ngFor="let col of primaryCollisions" class="csp__collision">
                  <div class="csp__collision-type">
                    <i class="fa" [ngClass]="{
                      'fa-lock': col.type === 'SlotBusy',
                      'fa-hand-paper': col.type === 'PbReserved',
                      'fa-spinner': col.type === 'PbInCalculation'
                    }"></i>
                    {{ collisionTypeLabel(col) }}
                  </div>
                  <div class="csp__collision-user">
                    <i class="fa fa-user"></i>
                    <strong>{{ col.blockingUserName || col.blockingUserId }}</strong>
                  </div>
                  <div class="csp__collision-detail" *ngIf="col.operationType">
                    {{ tr('COLLISION_ACTION') }} {{ col.operationType }}
                    <span *ngIf="col.truckName"> on {{ col.truckName }}</span>
                  </div>
                  <div class="csp__collision-detail" *ngIf="col.since">
                    {{ tr('COLLISION_SINCE') }} {{ formatTime(col.since) }}
                  </div>
                  <div class="csp__collision-detail" *ngIf="col.expiresAt">
                    {{ tr('COLLISION_EXPIRES') }} {{ formatTime(col.expiresAt) }}
                  </div>
                  <div class="csp__collision-pbs" *ngIf="col.conflictingPbIds?.length">
                    {{ tr('COLLISION_AFFECTED') }} {{ col.conflictingPbIds.join(', ') }}
                  </div>
                  <div class="csp__collision-msg">{{ col.userFriendlyMessage }}</div>
                </div>
              </ng-container>
              <ng-template #genericError>
                <div class="csp__section-title">{{ tr('SECTION_ERROR') }}</div>
                <div class="csp__error">
                  {{ primaryOp?.result?.errorMessage || tr('DEFAULT_ERROR') }}
                  <i class="fa fa-copy csp__copy-icon" (click)="copyError($event)" [title]="tr('COPY_ERROR')"></i>
                </div>
              </ng-template>
            </ng-container>
          </div>
        </div>

        <!-- Alerts -->
        <div class="csp__alerts" *ngIf="conflicts.length > 0">
          <div *ngFor="let c of conflicts" class="csp__alert" [ngClass]="{
            'csp__alert--warn': c.severity === 'warning',
            'csp__alert--err': c.severity === 'error'
          }">
            <i class="fa" [ngClass]="c.severity === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'"></i>
            {{ c.description }}
            <span class="csp__alert-res" *ngIf="c.resource">({{ c.resourceType || tr('DEFAULT_RESOURCE') }}: {{ c.resource }})</span>
          </div>
        </div>

        <!-- Algorithm timeline -->
        <div class="csp__algo">
          <div class="csp__algo-header">
            <div>
              <div class="csp__algo-label">{{ tr('ALGO_LABEL') }}</div>
              <div class="csp__algo-name">{{ tr('ALGO_STANDARD') }}</div>
            </div>
            <span class="csp__algo-time">
              {{ primaryOp?.status === 'Calculating' ? elapsedSeconds + 's' : durationDisplay }}
            </span>
          </div>

          <!-- Phase bar -->
          <div class="csp__phases">
            <div *ngFor="let phase of algoPhases"
                 class="csp__phase"
                 [ngClass]="{
                   'csp__phase--done': primaryOp?.status === 'Completed',
                   'csp__phase--fail': primaryOp?.status === 'Failed',
                   'csp__phase--active': primaryOp?.status === 'Calculating',
                   'csp__phase--pending': !primaryOp?.status
                 }"
                 [style.flex]="phase.pct"
                 [title]="phase.label + ' (~' + phase.pct + '% of total time)'">
            </div>
          </div>

          <!-- Phase labels -->
          <div class="csp__phase-labels">
            <div *ngFor="let phase of algoPhases"
                 class="csp__phase-label"
                 [ngClass]="{
                   'csp__phase-label--done': primaryOp?.status === 'Completed',
                   'csp__phase-label--fail': primaryOp?.status === 'Failed'
                 }"
                 [style.flex]="phase.pct">
              {{ phase.label }}
            </div>
          </div>
        </div>

        <!-- ═══ Queued action chips (visible while calculating) ═══ -->
        <div class="csp__queue" *ngIf="primaryOp?.status === 'Calculating'">
          <div class="csp__queue-title">{{ tr('QUEUE_TITLE') }}</div>
          <div class="csp__queue-chips">
            <button class="csp__chip"
                    [ngClass]="{'csp__chip--selected': primaryOp?.queuedAction === 'sendTo'}"
                    (click)="toggleQueuedAction('sendTo', $event)">
              <i class="fa fa-share-square"></i> {{ tr('QUEUE_SEND_TO') }}
            </button>
            <button class="csp__chip"
                    [ngClass]="{'csp__chip--selected': primaryOp?.queuedAction === 'sendPb'}"
                    (click)="toggleQueuedAction('sendPb', $event)">
              <i class="fa fa-sign-out-alt"></i> {{ tr('QUEUE_SEND_PB') }}
            </button>
          </div>
        </div>

        <!-- ═══ Queued action result (after completion) ═══ -->
        <div class="csp__queue-result" *ngIf="primaryOp?.queuedAction && primaryOp?.status !== 'Calculating'">
          <div class="csp__queue-result-header">
            <span class="csp__queue-result-label">{{ tr('QUEUE_LABEL') }} {{ queuedActionLabel }}</span>
            <span class="csp__queue-result-status" [ngClass]="{
              'csp__queue-result-status--pending': primaryOp?.queuedActionStatus === 'pending',
              'csp__queue-result-status--executing': primaryOp?.queuedActionStatus === 'executing',
              'csp__queue-result-status--ok': primaryOp?.queuedActionStatus === 'completed',
              'csp__queue-result-status--fail': primaryOp?.queuedActionStatus === 'failed' || primaryOp?.queuedActionStatus === 'skipped'
            }">
              <i class="fa fa-spinner fa-spin" *ngIf="primaryOp?.queuedActionStatus === 'executing'"></i>
              <i class="fa fa-check" *ngIf="primaryOp?.queuedActionStatus === 'completed'"></i>
              <i class="fa fa-times" *ngIf="primaryOp?.queuedActionStatus === 'failed'"></i>
              <i class="fa fa-ban" *ngIf="primaryOp?.queuedActionStatus === 'skipped'"></i>
              {{ queuedActionStatusLabel }}
            </span>
          </div>
          <div class="csp__queue-result-error" *ngIf="primaryOp?.queuedActionError">
            {{ primaryOp.queuedActionError }}
          </div>
          <div class="csp__queue-result-skip" *ngIf="(primaryOp?.status === 'Failed' && primaryOp?.queuedActionStatus === 'pending') || primaryOp?.queuedActionStatus === 'skipped'">
            {{ tr('QUEUE_SKIPPED') }}
          </div>
        </div>

        <!-- Previous ops on this truck -->
        <div class="csp__history" *ngIf="previousOps.length > 0">
          <div class="csp__history-title">{{ tr('PREVIOUS') }}</div>
          <div *ngFor="let op of previousOps" class="csp__history-item">
            <span *ngIf="op.status === 'Completed'" class="csp__history-icon csp__history-icon--ok">&#10003;</span>
            <span *ngIf="op.status === 'Failed'" class="csp__history-icon csp__history-icon--fail">&#10007;</span>
            <span>{{ formatTime(op.startedAt) }} · {{ getTypeLabel(op) }}</span>
            <span class="csp__history-dur" *ngIf="op.durationMs"> · {{ (op.durationMs / 1000).toFixed(1) }}s</span>
          </div>
        </div>

        <!-- Actions + auto-clear countdown -->
        <div class="csp__footer" *ngIf="primaryOp?.status === 'Completed' || primaryOp?.status === 'Failed'">
          <span class="csp__auto-clear" *ngIf="autoClearSeconds > 0">
            {{ tr('AUTO_CLEARS_IN') }} {{ autoClearDisplay }}
          </span>
          <button class="csp__btn csp__btn--clear" (click)="clearOp($event)">
            <i class="fa fa-check"></i> {{ tr('CLEAR') }}
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./calc-slot-indicator.component.scss']
})
export class CalcSlotIndicatorComponent implements OnInit, OnDestroy {
  @Input() truckId: string = '';
  @Input() truckName: string = '';
  @Input() driverName: string = '';
  @Input() planDate: string = '';         // Day view: current plan date
  @Input() planDateFrom: string = '';     // Week view: range start
  @Input() planDateUntil: string = '';    // Week view: range end

  slotOperations: CalculationOperation[] = [];
  showPopover = false;
  pinned = false;
  now = Date.now();
  popoverPos = { top: 0, left: 0 };
  focusedOperationId: string | null = null;

  private sub: Subscription;
  private tickSub: Subscription;
  private pinSub: Subscription;
  private hoverTimeout: any;

  constructor(
    private calcState: CalculationStateService,
    private elRef: ElementRef,
    private planningActionService: PlanningActionService,
    private planningBoardFacade: PlanningBoardFacade,
    private t: TranslateService
  ) {}

  /** Check if an operation's display date falls within the current view date.
   *  Prefers uiPlanDate (UI-only, reflects the action's actual target day)
   *  and falls back to planDate (which is also the value sent to the backend). */
  private _isCurrentDate(op: CalculationOperation): boolean {
    const displayDate = op.uiPlanDate || op.planDate;
    if (!displayDate) return true;
    const opDate = this._normalizeDate(displayDate);
    if (!opDate) return true;
    if (this.planDateFrom && this.planDateUntil) {
      const from = this._normalizeDate(this.planDateFrom);
      const until = this._normalizeDate(this.planDateUntil);
      if (from && until) return opDate >= from && opDate <= until;
    }
    if (this.planDate) {
      const current = this._normalizeDate(this.planDate);
      if (current) return opDate === current;
    }
    return true;
  }

  private _normalizeDate(dateStr: string): string | null {
    if (!dateStr) return null;
    if (dateStr.includes('T') || dateStr.match(/^\d{4}-\d{2}-\d{2}/)) return dateStr.substring(0, 10);
    const parts = dateStr.split('/');
    if (parts.length === 3 && parts[0].length <= 2) return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    return null;
  }

  /** Shorthand for translation */
  private tr(key: string, params?: object): string {
    return this.t.instant('OPTIMISTIC.' + key, params);
  }

  private previousStatuses: Map<string, string> = new Map();

  ngOnInit() {
    // Pin this popover when the activity panel navigates to this slot;
    // unpin any previously pinned slot so only one is ever visible at a time.
    this.pinSub = this.calcState.pinSlot$.subscribe(({ truckName, driverName, operationId }) => {
      const matches =
        (truckName  && this.truckName  && this.truckName  === truckName) ||
        (driverName && this.driverName && this.driverName === driverName);
      if (matches && this.slotOperations.length > 0) {
        this.focusedOperationId = operationId || null;
        this.calcPopoverPosition();
        this.pinned = true;
        this.showPopover = true;
        this.startPositionTracking();
        this.calcState.setPinnedSlot(truckName, driverName);
        (this.elRef.nativeElement as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (this.pinned) {
        this.pinned = false;
        this.showPopover = false;
        this.focusedOperationId = null;
        this.stopPositionTracking();
      }
    });

    this.sub = this.calcState.operations$.subscribe(ops => {
      const filtered = ops.filter(op =>
        ((op.truckId && this.truckId && op.truckId === this.truckId) ||
        (op.truckName && this.truckName && op.truckName === this.truckName) ||
        (op.driverName && this.driverName && op.driverName === this.driverName)) &&
        this._isCurrentDate(op)
      );

      // Detect status transitions: Calculating → Completed with a queued action
      for (const op of filtered) {
        const prevStatus = this.previousStatuses.get(op.operationId);
        if (prevStatus === 'Calculating' && op.status === 'Completed' && op.queuedAction && op.queuedActionStatus === 'pending') {
          // Use setTimeout to avoid mutating state inside subscribe
          setTimeout(() => this.executeQueuedAction(op), 0);
        }
        if (prevStatus === 'Calculating' && op.status === 'Failed' && op.queuedAction && op.queuedActionStatus === 'pending') {
          // Planning failed before queued action could execute.
          // Mark explicitly skipped so UI does not remain in "waiting".
          setTimeout(() => this.calcState.updateQueuedActionStatus(op.operationId, 'skipped'), 0);
        }
        this.previousStatuses.set(op.operationId, op.status);
      }

      this.slotOperations = filtered;
      
      // If pinned and no more operations, close
      if (this.showPopover && this.slotOperations.length === 0) {
        this.showPopover = false;
        this.pinned = false;
      }
    });
    this.tickSub = interval(1000).subscribe(() => {
      this.now = Date.now();
      this.calcState.expireStuckOperations();
      this.slotOperations.forEach(op => {
        if (op.expiresAt && new Date(op.expiresAt).getTime() <= this.now) {
          this.calcState.dismissOperation(op.operationId).subscribe();
        }
        if (op.queuedActionStatus === 'executing' && op.completedAt) {
          const completedTime = new Date(op.completedAt).getTime();
          if (this.now - completedTime > 30_000) {
            console.warn('[QUEUED-ACTION] Auto-completing stuck executing action:', op.operationId);
            this.calcState.updateQueuedActionStatus(op.operationId, 'completed');
          }
        }
      });
    });
  }

  ngOnDestroy() {
    if (this.pinned) this.calcState.clearPinnedSlot();
    this.sub?.unsubscribe();
    this.tickSub?.unsubscribe();
    this.pinSub?.unsubscribe();
    this.stopPositionTracking();
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
  }

  // ── Dot state (handles string "Completed" from local state AND numeric 1 from API) ──
  // .NET enum: 0=Calculating, 1=Completed, 2=Failed
  private statusIs(op: any, s: string, n: number): boolean {
    return op.status === s || op.status === n || String(op.status).toLowerCase() === s.toLowerCase();
  }
  get hasActive(): boolean { return this.slotOperations.some(op => this.statusIs(op, 'Calculating', 0)); }
  get hasCompleted(): boolean { return this.slotOperations.some(op => this.statusIs(op, 'Completed', 1)); }
  get hasFailed(): boolean {
    return this.slotOperations.some(op => this.statusIs(op, 'Failed', 2)) || this.hasResultError;
  }
  get hasResultError(): boolean {
    return this.slotOperations.some(op =>
      this.statusIs(op, 'Completed', 1) && op.result?.success === false
    );
  }
  get hasWarnings(): boolean {
    return this.slotOperations.some(op =>
      this.statusIs(op, 'Completed', 1) &&
      op.result?.success !== false &&
      op.result?.conflicts?.some(c => c.severity === 'warning' || c.severity === 'error')
    );
  }
  /** True if any operation's queued action failed (calc succeeded but send failed) */
  get hasQueuedFailed(): boolean {
    return this.slotOperations.some(op => op.queuedActionStatus === 'failed' || op.queuedActionStatus === 'skipped');
  }
  /** True if any operation's queued action is currently executing */
  get hasQueuedExecuting(): boolean {
    return this.slotOperations.some(op => op.queuedActionStatus === 'executing');
  }

  // ── Primary operation (focused by activity panel click, or most recent) ──
  get primaryOp(): CalculationOperation | null {
    if (this.focusedOperationId) {
      const focused = this.slotOperations.find(op => op.operationId === this.focusedOperationId);
      if (focused) return focused;
    }
    return this.slotOperations[0] || null;
  }
  get previousOps(): CalculationOperation[] {
    const primary = this.primaryOp;
    return this.slotOperations.filter(op => op !== primary);
  }

  // ── Display helpers ──
  get statusLabel(): string {
    if (!this.primaryOp) return '';
    const isPlanAction = ['plan', 'move', 'assign'].includes(this.primaryOp.operationType);
    if (this.primaryOp.status === 'Calculating') return isPlanAction ? this.tr('STATUS_PLANNING') : this.tr('STATUS_PROCESSING');
    if (this.primaryOp.status === 'Completed') {
      if (this.primaryOp.result?.success === false) return this.tr('STATUS_FAILED');
      if (this.primaryOp.queuedActionStatus === 'completed') return this.tr('STATUS_SENT');
      return isPlanAction ? this.tr('STATUS_PLANNED') : this.tr('STATUS_DONE');
    }
    if (this.primaryOp.status === 'Failed') return this.tr('STATUS_FAILED');
    return '';
  }

  get subtitleLine(): string {
    if (!this.primaryOp) return '';
    const parts: string[] = [];
    if (this.primaryOp.driverName) parts.push(this.primaryOp.driverName);
    if (this.primaryOp.truckName) parts.push(this.primaryOp.truckName);
    parts.push(this.getTypeLabel(this.primaryOp));
    if (this.primaryOp.planDate) parts.push(this.primaryOp.planDate);
    return parts.filter(p => p).join(' · ');
  }

  get elapsedSeconds(): number {
    if (!this.primaryOp) return 0;
    return Math.floor((this.now - new Date(this.primaryOp.startedAt).getTime()) / 1000);
  }

  get durationDisplay(): string {
    if (!this.primaryOp?.durationMs) return '';
    return (this.primaryOp.durationMs / 1000).toFixed(1) + 's';
  }

  get timingLabel(): string {
    if (!this.primaryOp) return '';
    if (this.primaryOp.status === 'Calculating') return this.elapsedSeconds + 's';
    return this.durationDisplay;
  }

  get pbDetails() { return this.primaryOp?.planningBlockDetails || []; }
  get requestedBy(): string { return this.primaryOp?.userName || this.primaryOp?.userId || ''; }
  get conflicts() { return this.primaryOp?.result?.conflicts || []; }
  get summary() { return this.primaryOp?.result?.summary || null; }
  get primaryCollisions(): PlanningCollision[] { return this.primaryOp?.collisions || []; }

  collisionTypeLabel(col: PlanningCollision): string {
    switch (col.type) {
      case 'SlotBusy': return this.tr('COLLISION_ROW_BUSY');
      case 'PbReserved': return this.tr('COLLISION_PB_RESERVED');
      case 'PbInCalculation': return this.tr('COLLISION_PB_IN_CALC');
      default: return this.tr('COLLISION_DEFAULT');
    }
  }

  // ── Algorithm phases (Standard Planning) ──
  // Based on fctUpdateInfoOnTO() analysis - 14 BC sub-functions grouped into user-facing phases
  // Percentages reflect real-world timing: calculating times is the dominant bottleneck
  get algoPhases() {
    return [
      { label: this.tr('ALGO_CREATING'),           pct: 5  },
      { label: this.tr('ALGO_PLANNING_ROUTE'),     pct: 8  },
      { label: this.tr('ALGO_CALC_TIMES'),         pct: 45 },
      { label: this.tr('ALGO_COSTS_CAPACITY'),     pct: 20 },
      { label: this.tr('ALGO_CHECKING_CONFLICTS'), pct: 17 },
      { label: this.tr('ALGO_FINALIZING'),         pct: 5  },
    ];
  }


  // ── Auto-clear countdown ──
  get autoClearSeconds(): number {
    if (!this.primaryOp?.expiresAt) return 0;
    const remaining = new Date(this.primaryOp.expiresAt).getTime() - this.now;
    return Math.max(0, Math.floor(remaining / 1000));
  }

  get autoClearDisplay(): string {
    const s = this.autoClearSeconds;
    if (s <= 0) return '';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
  }

  getTypeLabel(op: CalculationOperation): string {
    const map: Record<string, string> = {
      plan: 'TYPE_PLAN', move: 'TYPE_MOVE', assign: 'TYPE_ASSIGN',
      sendPb: 'TYPE_SEND_PB', cancelPb: 'TYPE_CANCEL_PB', unplanPb: 'TYPE_UNPLAN_PB',
      cancelAndUnplanPb: 'TYPE_CANCEL_AND_UNPLAN_PB', replanPb: 'TYPE_REPLAN_PB',
      cancelAndReplanPb: 'TYPE_CANCEL_AND_REPLAN_PB',
      sendTo: 'TYPE_SEND_TO', cancelTo: 'TYPE_CANCEL_TO', unplanTo: 'TYPE_UNPLAN_TO',
      cancelAndUnplanTo: 'TYPE_CANCEL_AND_UNPLAN_TO', resendTo: 'TYPE_RESEND_TO',
      sendNextPb: 'TYPE_SEND_NEXT_PB', removeCapacity: 'TYPE_REMOVE_CAPACITY'
    };
    return this.tr(map[op.operationType] || 'TYPE_DEFAULT');
  }

  getSectionTitle(op: CalculationOperation): string {
    const map: Record<string, string> = {
      plan: 'PHASE_PLANNING', move: 'PHASE_MOVING', assign: 'PHASE_ASSIGNING',
      sendPb: 'PHASE_SENDING', cancelPb: 'PHASE_CANCELLING', unplanPb: 'PHASE_UNPLANNING',
      cancelAndUnplanPb: 'PHASE_CANCELLING_UNPLANNING', replanPb: 'PHASE_REPLANNING',
      cancelAndReplanPb: 'PHASE_CANCELLING_REPLANNING',
      sendTo: 'PHASE_SENDING', cancelTo: 'PHASE_CANCELLING', unplanTo: 'PHASE_UNPLANNING',
      cancelAndUnplanTo: 'PHASE_CANCELLING_UNPLANNING', resendTo: 'PHASE_SENDING',
      sendNextPb: 'PHASE_SENDING', removeCapacity: 'PHASE_UNPLANNING'
    };
    return this.tr(map[op.operationType] || 'PHASE_PLANNING');
  }

  formatTime(isoString: string): string {
    if (!isoString) return '';
    return new Date(isoString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  // ── Position (fixed, escapes parent overflow) ──

  private rafId: number | null = null;

  get popoverStyle() {
    return {
      position: 'fixed',
      top: this.popoverPos.top + 'px',
      left: this.popoverPos.left + 'px'
    };
  }

  private calcPopoverPosition(): void {
    const el = this.elRef.nativeElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.popoverPos = {
      top: rect.top + rect.height + 4,
      left: rect.right + 8
    };
    const popoverWidth = 520;
    if (this.popoverPos.left + popoverWidth > window.innerWidth - 16) {
      this.popoverPos.left = Math.max(16, window.innerWidth - popoverWidth - 16);
    }
    const popoverHeight = 350;
    if (this.popoverPos.top + popoverHeight > window.innerHeight - 16) {
      this.popoverPos.top = Math.max(16, rect.top - popoverHeight - 4);
    }
  }

  /** When pinned, track the dot position every frame so the popover follows scrolling */
  private startPositionTracking(): void {
    this.stopPositionTracking();
    const track = () => {
      if (this.pinned && this.showPopover) {
        this.calcPopoverPosition();
        this.rafId = requestAnimationFrame(track);
      }
    };
    this.rafId = requestAnimationFrame(track);
  }

  private stopPositionTracking(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // ── Hover / Click / Pin ──

  onHoverEnter(): void {
    if (this.hoverTimeout) { clearTimeout(this.hoverTimeout); this.hoverTimeout = null; }
    // Block hover when a different slot is already pinned
    const pinned = this.calcState.pinnedSlot;
    const isThisSlotPinned = pinned &&
      ((pinned.truckName  && this.truckName  && pinned.truckName  === this.truckName) ||
       (pinned.driverName && this.driverName && pinned.driverName === this.driverName));
    if (pinned && !isThisSlotPinned) return;
    if (!this.pinned) {
      this.focusedOperationId = null;
      this.calcPopoverPosition();
      this.showPopover = true;
    }
  }

  onHoverLeave(): void {
    if (!this.pinned) {
      // Small delay so user can move mouse into popover
      this.hoverTimeout = setTimeout(() => {
        if (!this.pinned) this.showPopover = false;
      }, 200);
    }
  }

  onPopoverEnter(): void {
    // Cancel the hide timeout when mouse enters popover
    if (this.hoverTimeout) { clearTimeout(this.hoverTimeout); this.hoverTimeout = null; }
  }

  onPopoverLeave(): void {
    if (!this.pinned) {
      this.hoverTimeout = setTimeout(() => {
        if (!this.pinned) this.showPopover = false;
      }, 200);
    }
  }

  onDotClick(event: Event): void {
    event.stopPropagation();
    if (this.pinned) {
      this.pinned = false;
      this.showPopover = false;
      this.focusedOperationId = null;
      this.stopPositionTracking();
      this.calcState.clearPinnedSlot();
    } else {
      // Pin — first broadcast so any other pinned slot unpins itself
      this.calcState.pinSlot(this.truckName, this.driverName);
      this.calcPopoverPosition();
      this.pinned = true;
      this.showPopover = true;
      this.startPositionTracking();
    }
  }

  closePopover(event: Event): void {
    event.stopPropagation();
    this.pinned = false;
    this.showPopover = false;
    this.focusedOperationId = null;
    this.stopPositionTracking();
    this.calcState.clearPinnedSlot();
  }

  // ── Queued Actions ──

  toggleQueuedAction(action: QueuedActionType, event: Event): void {
    event.stopPropagation();
    if (!this.primaryOp) return;
    const newAction = this.primaryOp.queuedAction === action ? null : action;
    this.calcState.setQueuedAction(this.primaryOp.operationId, newAction);
  }

  get queuedActionLabel(): string {
    switch (this.primaryOp?.queuedAction) {
      case 'sendTo': return this.tr('QUEUE_SEND_TO');
      case 'sendPb': return this.tr('QUEUE_SEND_PB');
      case 'resendTo': return this.tr('QUEUE_SEND_TO');
      default: return '';
    }
  }

  get queuedActionStatusLabel(): string {
    switch (this.primaryOp?.queuedActionStatus) {
      case 'pending': return this.tr('QUEUE_WAITING');
      case 'executing': return this.tr('QUEUE_EXECUTING');
      case 'completed': return this.tr('QUEUE_DONE');
      case 'failed': return this.tr('QUEUE_FAILED');
      case 'skipped': return this.tr('QUEUE_SKIPPED');
      default: return '';
    }
  }

  /** Called when an operation transitions to Completed — executes queued action if set */
  private executeQueuedAction(op: CalculationOperation): void {
    if (!op.queuedAction || op.queuedActionStatus !== 'pending') return;
    if (op.status === 'Failed') return;
    if (op.result?.success === false) {
      this.calcState.updateQueuedActionStatus(op.operationId, 'skipped');
      return;
    }

    const toId = op.result?.createdTransportOrderId;
    if (!toId) {
      this.calcState.updateQueuedActionStatus(op.operationId, 'failed', this.tr('NO_TO_AVAILABLE'));
      return;
    }

    this.calcState.updateQueuedActionStatus(op.operationId, 'executing');
    console.log('[QUEUED-ACTION] Executing:', op.queuedAction, '| Op:', op.operationId, '| TO:', toId);

    // Fetch the TO to get full data for the action
    this.planningActionService.getTransportOrderById(toId).subscribe(
      (to) => {
        if (!to) {
          this.calcState.updateQueuedActionStatus(op.operationId, 'failed', this.tr('TO_NOT_FOUND'));
          return;
        }

        let action$;
        switch (op.queuedAction) {
          case 'sendTo':
            action$ = this.planningActionService.sendTo([to]);
            break;
          case 'sendPb':
            action$ = this.planningActionService.sendNextPb([to]);
            break;
          case 'resendTo':
            action$ = this.planningActionService.resendTo([to]);
            break;
          default:
            this.calcState.updateQueuedActionStatus(op.operationId, 'failed', this.tr('UNKNOWN_ACTION'));
            return;
        }

        let actionHandled = false;
        action$.pipe(
          finalize(() => {
            if (!actionHandled) {
              console.warn('[QUEUED-ACTION] Observable completed without next/error — forcing completed:', op.operationId);
              this.calcState.updateQueuedActionStatus(op.operationId, 'completed');
              this.refreshPlanningBoardAfterQueuedAction(toId);
            }
          })
        ).subscribe({
          next: () => {
            actionHandled = true;
            console.log('[QUEUED-ACTION] Success:', op.queuedAction, '| Op:', op.operationId);
            this.calcState.updateQueuedActionStatus(op.operationId, 'completed');
            this.refreshPlanningBoardAfterQueuedAction(toId);
          },
          error: (err) => {
            actionHandled = true;
            console.error('[QUEUED-ACTION] Failed:', op.queuedAction, '| Op:', op.operationId, err);
            this.calcState.updateQueuedActionStatus(op.operationId, 'failed',
              err?.error?.message || err?.message || 'Action failed');
          }
        });
      },
      (err) => {
        this.calcState.updateQueuedActionStatus(op.operationId, 'failed', 'Could not load transport order');
      }
    );
  }

  /**
   * Force an immediate planning board refresh after queued actions complete
   * so PB status/send indicators are up-to-date without waiting for monitor updates.
   */
  private refreshPlanningBoardAfterQueuedAction(toId: string): void {
    if (!toId) return;
    this.planningActionService.getTransportOrderById(toId).subscribe((freshTo) => {
      if (!freshTo) return;
      this.planningBoardFacade.refreshOnPlanningActionForTransportOrder(freshTo);
      this.planningBoardFacade.storeUnplannedPlanningBlocks();
    });
  }

  clearOp(event: Event): void {
    event.stopPropagation();
    if (this.primaryOp) {
      this.calcState.verifyOperation(this.primaryOp.operationId);
      this.calcState.dismissOperation(this.primaryOp.operationId).subscribe();
    }
  }

  copyError(event: Event): void {
    event.stopPropagation();
    const msg = this.primaryOp?.result?.errorMessage || '';
    if (msg && navigator.clipboard) {
      navigator.clipboard.writeText(msg);
    }
  }
}
